import React, { useContext } from 'react';
import styled, { css } from 'styled-components';

import withFallback from '../../common/image-with-fallback';
import stripHtmlToText from '../../common/utils/strip-html';
import {
  remainingOfferAmount,
  getProductOriginalPrice,
  offerOverThreshold,
} from '../contexts/products';
import CurrencyContext from '../contexts/CurrencyContext';
import CartContext from '../contexts/CartContext';
import AnimatedCheckmark from './elements/AnimatedCheckmark';
import Link, { HTMLSectionWithLink } from './elements/Link';
import MoneyFormat from './elements/MoneyFormat';
import handleConfirmation from '../utils/adaHandler';

export const CardElement = styled.div.attrs((props) => ({
  className:
    `GiftBox-Card Offer-Id-${props.offerId}` +
    (props.added ? ' GiftBox-Card-Eligible' : ''),
}))`
  min-height: 104px;
  margin: 0;
  margin-bottom: 17px;
  background: #fff;
  border-radius: 5px;
  box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.1);
  position: relative;

  ${(props) =>
    props.added &&
    css`
      background: rgba(81, 184, 60, 0.05);
      border: 1px solid rgba(49, 205, 102, 0.3);
      box-shadow: 0 2px 8px 0 rgba(81, 184, 60, 0.15);
    `};

  ${(props) =>
    props.isMultipleProduct &&
    css`
      background: #fff;
    `};
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 20px 5px 20px;
  line-height: 1.4;
  flex-shrink: 0;
  flex-wrap: wrap;

  ${(props) =>
    props.isMultipleProduct &&
    css`
      padding-bottom: 20px;
    `};
`;

const Img = styled.div.attrs(() => ({
  className: 'GiftBox-Card-Img',
}))`
  position: relative;
  display: flex;
  align-items: center;
  img,
  & > svg {
    width: 40px;
    height: 40px;
    object-fit: cover;
  }
`;

const Description = styled.div.attrs(() => ({
  className: 'GiftBox-Card-Description',
}))`
  margin-left: 20px;
  max-width: calc(100% - 70px);
`;

export const Title = styled.h3``;

const Line = styled.div`
  color: #616161;
`;

const OriginalPrice = styled.span.attrs(() => ({
  className: 'GiftBox-Card-OriginalPrice',
}))`
  font-size: 12px;
  text-decoration: line-through;
  color: #939393;
`;

const DiscountedPrice = styled.span.attrs(() => ({
  className: 'GiftBox-Card-DiscountedPrice',
}))`
  font-size: 12px;
  color: rgba(49, 205, 102, 1);
`;

export const ProductCardSection = styled(
  ({
    className,
    variant,
    product,
    currency,
    onClick,
    passed,
    display_product_names_as_links,
  }) => (
    <div className={`${className} ProductCardSection Product-Id-${product.id}`}>
      <Wrapper
        data-cy="multiple-offer-popup"
        onClick={onClick}
        tabIndex={0}
        onKeyPress={handleConfirmation(onClick)}
      >
        <Img>
          <img
            src={withFallback(
              (variant &&
                variant.featured_image &&
                variant.featured_image.src) ||
                product.featured_image,
            )}
            alt="Product"
          />
        </Img>
        <Description>
          <Line>
            {passed || !display_product_names_as_links ? (
              stripHtmlToText(product.title)
            ) : (
              <Link key="link" href={product.url}>
                {stripHtmlToText(product.title)}
              </Link>
            )}{' '}
            <OriginalPrice>
              <MoneyFormat
                amount={getProductOriginalPrice(variant)}
                currency={currency}
              />
            </OriginalPrice>
            &nbsp;
            <DiscountedPrice>
              <MoneyFormat amount={0} currency={currency} />
            </DiscountedPrice>
          </Line>
        </Description>
      </Wrapper>
    </div>
  ),
)`
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  ${(props) =>
    props.passed
      ? css`
          border-top: 1px solid rgba(49, 205, 102, 0.3);
          &:hover {
            cursor: pointer;
            background: rgba(81, 184, 60, 0.05);
          }
        `
      : css`
          background: #fafafa;
          border-bottom-left-radius: 5px;
          border-bottom-right-radius: 5px;
        `}

  & > div {
    padding: 15px 20px;
  }
`;

export function Threshold({ offer, children }) {
  const { cart, collectionsInCart } = useContext(CartContext);
  const currencyCtx = useContext(offer.isAdminOffer ? {} : CurrencyContext);
  const currency = offer.isAdminOffer ? offer.currency : currencyCtx;

  if (!cart) {
    return null;
  }

  const amount = remainingOfferAmount(offer, cart, collectionsInCart);
  const cartEmpty = cart.item_count === 0;
  const remainingSpend = MoneyFormat({ amount, currency });
  const passed = offerOverThreshold(offer, cart, collectionsInCart);
  let titleText = getTitleText(offer, passed, cartEmpty, remainingSpend);

  return children({
    passed,
    titleText,
  });
}

/**
 * Card.js is also used by admin. Admin doesn't have same origin so we need to get it via DATA object
 */
function getShopUrl() {
  const shop = window.Shopify?.shop || window.DATA?.SHOP || 'unknown_url';
  return shop;
}

function getTitleText(offer, passed, cartEmpty, remainingSpend) {
  if (passed) {
    if (offer.products && !offer.shopify_product_variant_id) {
      return (
        offer.title_over_threshold_product_not_selected ||
        'Great! Please select your free gift below'
      );
    }
    return offer.title_over_threshold || 'You got this! 🎉';
  }

  if (offer.threshold_mode && offer.threshold_mode === 'product_added') {
    return getTitleTextProduct(offer);
  }
  if (offer.threshold_mode && offer.threshold_mode === 'collection_added') {
    return getTitleTextCollection(offer);
  }
  if (offer.threshold_mode && offer.threshold_mode === 'spend_in_collection') {
    return getTitleTextSpendInCollection(offer, cartEmpty, remainingSpend);
  }
  return getTitleTextFreeFrom(offer, cartEmpty, remainingSpend);
}

function getTitleTextFreeFrom(offer, cartEmpty, remainingSpend) {
  if (cartEmpty) {
    return (offer.title_empty_cart || 'Spend REMAININGSPEND').replace(
      'REMAININGSPEND',
      remainingSpend,
    );
  }

  return (
    offer.title_under_threshold || 'Spend only REMAININGSPEND more'
  ).replace('REMAININGSPEND', remainingSpend);
}

function getTitleTextProduct(offer) {
  const text =
    offer.title_under_threshold ||
    (offer.target_min_quantity > 1
      ? 'Buy REQPRODUCTQx REQPRODUCTLINK'
      : 'Buy REQPRODUCTLINK');
  const parts = text
    .replace('REQPRODUCTQ', String(offer.target_min_quantity))
    .split(/REQ(?:UIRED)?PRODUCTLINK/);
  if (parts.length === 1) {
    return parts[0];
  }

  parts.splice(
    1,
    0,
    `<a href="https://${getShopUrl()}/products/${
      offer.target_shopify_product_handle
    }">${offer.target_shopify_product_name}</a>`,
  );
  return parts.join('');
}

function getTitleTextCollection(offer) {
  const text =
    offer.title_under_threshold ||
    (offer.target_min_quantity > 1
      ? 'Buy REQPRODUCTQx products from REQCOLLECTIONLINK collection'
      : 'Buy a product from REQCOLLECTIONLINK collection');
  const parts = text
    .replace('REQPRODUCTQ', String(offer.target_min_quantity))
    .split(/REQ(?:UIRED)?COLLECTIONLINK/);
  if (parts.length === 1) {
    return parts[0];
  }

  parts.splice(
    1,
    0,
    `<a href="https://${getShopUrl()}/collections/${
      offer.target_shopify_collection_handle
    }">${offer.target_shopify_collection_name}</a>`,
  );
  return parts.join('');
}

function getTitleTextSpendInCollection(offer, cartEmpty, remainingSpend) {
  let text = '';
  if (cartEmpty) {
    text =
      offer.title_empty_cart ||
      'Spend REMAININGSPEND in REQCOLLECTIONLINK collection';
  } else {
    text =
      offer.title_under_threshold ||
      'Spend REMAININGSPEND more in REQCOLLECTIONLINK collection';
  }
  const parts = text
    .replace('REMAININGSPEND', String(remainingSpend))
    .split(/REQ(?:UIRED)?COLLECTIONLINK/);

  if (parts.length === 1) {
    return parts[0];
  }

  parts.splice(
    1,
    0,
    `<a href="https://${getShopUrl()}/collections/${
      offer.target_shopify_collection_handle
    }">${offer.target_shopify_collection_name}</a>`,
  );
  return parts.join('');
}

export default ({
  offer,
  children,
  image,
  originalPrice,
  content,
  baseContent,
  isMultipleProduct,
  animatedCheckmarkOverride,
}) => {
  const currencyCtx = useContext(offer.isAdminOffer ? {} : CurrencyContext);
  const currency = offer.isAdminOffer ? offer.currency : currencyCtx;

  const animatedCheckmark = animatedCheckmarkOverride || (
    <AnimatedCheckmark animate={offer.justAdded} />
  );

  return (
    <Threshold offer={offer}>
      {({ passed, titleText }) => (
        <CardElement
          added={passed}
          isMultipleProduct={isMultipleProduct}
          offerId={offer.id}
        >
          <Wrapper isMultipleProduct={isMultipleProduct}>
            <Img>
              {image}
              {passed && !isMultipleProduct ? animatedCheckmark : null}
            </Img>
            <Description>
              <Title>
                <HTMLSectionWithLink>{titleText}</HTMLSectionWithLink>
              </Title>
              <Line className="GiftBox-CardLine">{children}</Line>
              {originalPrice ? (
                <Line>
                  <OriginalPrice>
                    <MoneyFormat amount={originalPrice} currency={currency} />
                  </OriginalPrice>
                  &nbsp;
                  <DiscountedPrice>
                    <MoneyFormat amount={0} currency={currency} />
                  </DiscountedPrice>
                </Line>
              ) : null}
            </Description>
            {content}
          </Wrapper>
          {baseContent}
        </CardElement>
      )}
    </Threshold>
  );
};
